import React, { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useOutletContext } from 'react-router-dom';
import { TDR } from 'tdr-common';
import { verifyCode } from '../../api/guest';
import { useCheckoutContext } from '../../context/new/CheckoutContext';
import { analytics } from '../../helpers/analytics';
import { countryCodeConverter } from '../../helpers/countryCodeConverter';
import { RestaurantGuardOutletContext } from '../../routes/RestaurantGuard';
import AgreementCheckbox from '../agreement-checkbox/AgreementCheckbox';
import { EmailCheckInput } from '../form-inputs/EmailCheckInput';
import { RHFPhoneInput } from '../form-inputs/RHFPhoneInput';
import { RHFTextInput } from '../form-inputs/RHFTextInput';
import { SubHeader } from '../headers/SubHeader';
import { GuestInfo } from './types';

import { useTranslation } from 'react-i18next';
import layoutStyles from '../../layouts/CheckoutLayout.module.scss';
import styles from './ContactInput.module.scss';

export const ContactInput = () => {
	const { t } = useTranslation();
	const { restaurant, featureFlags } = useOutletContext<RestaurantGuardOutletContext>();
	const { updateCheckoutState, setHasContactDetailsInput } = useCheckoutContext();

	const [verifyingEmail, setVerifyingEmail] = useState<boolean>(false);
	const [guest, setGuest] = useState<TDR.Guest.Guest>(null);

	const {
		register,
		control,
		formState: { errors },
		setValue,
		trigger,
		watch
	} = useFormContext<GuestInfo>();

	const email = watch('email');
	const firstName = watch('firstName');
	const lastName = watch('lastName');
	const phone = watch('phone');

	useEffect(() => {
		const hasAnyContactInput = !!(email || firstName || lastName || phone);
		setHasContactDetailsInput(hasAnyContactInput);
	}, [email, firstName, lastName, phone, setHasContactDetailsInput]);

	const confirmationHandler = async (
		code: string,
		sessionId: string,
		setInvalidCode: React.Dispatch<React.SetStateAction<boolean>>
	) => {
		setInvalidCode(false);
		setVerifyingEmail(true);
		const res = await verifyCode({ code, sessionId });
		if (res.success) {
			if (res.token) {
				updateCheckoutState({
					firebaseToken: res.token
				});
			}
			if (!!res.guest) {
				analytics.track('Checkout Auto-Filled', { email: res.guest?.email, userId: res.guest?.id });
				setValue('firstName', res.guest.firstName);
				setValue('lastName', res.guest.lastName);
				setValue('phone', res.guest.phone);
				setValue('fastCheckoutEnabled', res.guest.hasFastCheckoutEnabled);
				await trigger(); //need to re-trigger rhf validation to get all the form states updated.
			}
			setGuest(res.guest);
		}
		else {
			setInvalidCode(true);
		}
		setVerifyingEmail(false);
	};

	return (
		<section className={`${layoutStyles.Section} !pt-8`}>
			<SubHeader title={t('titles.contact_information')} isRequired={true} />
			<span id='sign-in-button' style={{ display: 'none' }} />

			<div className={styles.ContactInfo}>
				<EmailCheckInput
					name='email'
					register={register}
					errors={errors}
					enableFastCheckout={featureFlags?.enableFastCheckout}
					disabled={verifyingEmail}
					onCodeSubmit={confirmationHandler}
					guest={guest}
				/>

				<div className={styles.Row}>
					<RHFTextInput label={t('forms.first_name')} name='firstName' register={register} error={errors.firstName?.message} />
					<RHFTextInput label={t('forms.last_name')} name='lastName' register={register} error={errors.lastName?.message} />
				</div>

				<RHFPhoneInput
					name='phone'
					control={control}
					label={t('forms.phone_number')}
					defaultCountry={countryCodeConverter(restaurant.country ?? restaurant.stripeRegion)}
					error={errors.phone?.message}
				/>
			</div>

			{/* Booking SMS Section */}
			<div className="pt-3 border-zinc-700">
				<AgreementCheckbox
					id='smsTransactionalConsent'
					checked={watch('smsTransactionalConsentGranted') || false}
					{...register('smsTransactionalConsentGranted')}
					label={t('consent.sms_booking', {
						defaultValue: 'Receive text message updates for this reservation (confirmations, reminders)'
					})}
					labelVariant='small'
				/>
			</div>
		</section>
	);
};
